import React, { useCallback, useContext } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from '../../offices/modals/modal.styles';
import * as companyStyles from '../companies.styles';
import { type CompanyOffice } from '../models/company';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import DeletionImpactSummary, { useDeletionImpact } from '../../offices/modals/deletion-impact-summary.component';
import { type RetirementImpact } from '../../offices/services/offices.service';
import { getCompanyDeletionImpact } from '../services/companies.service';

// Cap the number of office names spelled out in the modal so a company with
// many offices still yields a readable confirmation dialog.
const MAX_LISTED_OFFICES = 8;

interface Props {
  readonly showModal: boolean;
  readonly isLoading?: boolean;
  readonly companyId?: string;
  readonly itemName: string;
  readonly offices?: CompanyOffice[];
  readonly handleClose: () => void;
  /** Called only once the impact, if any, has been acknowledged. */
  readonly handleAccept: (acknowledged: boolean) => void;
}

const CompanyConfirmationModal = ({
  showModal,
  isLoading,
  companyId,
  itemName,
  offices,
  handleClose,
  handleAccept,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  // Every office in the company is retired as deleting it on its own would - so the same count, across all of them.
  const load = useCallback(async (): Promise<RetirementImpact | undefined> => {
    if (companyId == null) return undefined;
    const result = await getCompanyDeletionImpact(companyId, keycloak);
    return result?.impact;
  }, [companyId, keycloak]);

  const impactState = useDeletionImpact(showModal, companyId == null ? undefined : load);

  const listedOffices = offices?.slice(0, MAX_LISTED_OFFICES) ?? [];
  const remainingOfficeCount = (offices?.length ?? 0) - listedOffices.length;

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>
          {t('about-to-remove-company')} {itemName}
        </span>
        {listedOffices.length > 0 ? (
          <>
            <span className={style(styles.bodyText)}>{t('delete-company-offices-list-intro')}</span>
            <ul className={style(companyStyles.modalOfficeList)}>
              {listedOffices.map((office) => (
                <li key={office.id} className={style(styles.bodyText)}>
                  {office.name}
                </li>
              ))}
            </ul>
            {remainingOfficeCount > 0 && (
              <span className={style(styles.bodyText)}>{t('and-x-more-offices').replace('[x]', remainingOfficeCount.toString())}</span>
            )}
            <span className={style(styles.bodyText)}>{t('delete-company-cascade-warning')}</span>
          </>
        ) : (
          <span className={style(styles.bodyText)}>{t('no-linked-offices')}</span>
        )}
        <DeletionImpactSummary state={impactState} />
        <span className={style(styles.bodyText)}>{t('are-you-sure')}</span>

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              disabled={!impactState.confirmable || isLoading === true}
              onClick={(): void => {
                handleAccept(impactState.impact?.hasImpact ?? false);
              }}
              loading={isLoading}
            >
              {t('delete')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default CompanyConfirmationModal;
